import PageLayout from 'components/pages/PageLayout'
import React, { useContext, useEffect } from 'react'
import PageMeta from 'components/PageMeta'
import { useRouter } from 'next/router'
import { withApollo } from 'components/withApollo'
import { GetStaticPathsResult, NextPage } from 'next'
import LoadingPage from 'components/pages/LoadingPage'
import { useQuery } from '@apollo/client'
import ErrorPage from 'components/pages/ErrorPage'
import NotFoundPage from 'components/pages/NotFoundPage'
import {
  shiftPageQuery,
  shiftPageQueryVariables
} from 'components/pages/ShiftPage/types/shiftPageQuery'
import ShiftMenu from 'components/pages/ShiftPage/ShiftMenu'
import TruckInformationSection from 'components/HeroSection'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import TruckSectionContent from 'components/pages/ShiftPage/TruckSectionContent'
import AppAds from 'components/AppAds'
import useReferrals from 'hooks/useReferrals'
import { ShiftPageContext } from 'components/pages/ShiftPage/ShiftPageProvider'
import cn from 'classnames'
import pathFromUrl from 'utils/pathFromUrl'
import LinksSection from 'components/HeroSection/LinksSection'
import stripTimeZone from 'utils/stripTimeZone'

import SHIFT_QUERY from './shiftQuery'

type Props = {}

type Params = {
  locationId: string
}

const BaseShiftPage = () => {
  const { albertsons } = useContext(ShiftPageContext)

  const router = useRouter()
  const { locationId: slug } = router.query as Params

  const locationIdParam = slug.split('-')[0]
  const { data, loading, error } = useQuery<
    shiftPageQuery,
    shiftPageQueryVariables
  >(SHIFT_QUERY, {
    variables: {
      id: parseInt(locationIdParam, 10)
    },
    fetchPolicy: 'cache-and-network'
  })

  const location = data?.location

  useReferrals()
  // if location is not active redirect to lot
  useEffect(() => {
    if (location && !location.active) {
      window.location.href = location.lot.lotUrl!
    }
  }, [location, router])

  if (loading && !location) return <LoadingPage />
  const notFoundError =
    error &&
    error.message.includes(
      'Cannot return null for non-nullable field Query.location'
    )
  if (error && !notFoundError) return <ErrorPage message={error.message} />
  if (
    !location ||
    notFoundError ||
    !location.active ||
    !location.truck.active ||
    !location.truck.restaurant.active
  ) {
    return <NotFoundPage />
  }

  const heroLinks = {
    twitter: location.truck.twitter,
    facebook: location.truck.facebook,
    instagram: location.truck.instagram,
    website: location.truck.website
  }
  return (
    <PageLayout
      hideFooter={albertsons}
      hideNavigation={albertsons}
      highlightCartItems={false}
      fullWidth
      staticPage
    >
      <PageMeta
        canonicalPath={pathFromUrl(location.lot.lotUrl)}
        coordinates={[location.lot.latitude, location.lot.longitude]}
        description={`Order from ${location.truck.name} for a shift on ${format(
          parseISO(stripTimeZone(location.startTime)),
          'M/d/y'
        )} at ${location.lot.name}, ${location.lot.fullAddress}`}
        image={location.foodImage.medium}
        ldJson={data?.structuredData?.shiftPage}
        path={`/shifts/${slug}`}
        title={`Best Food Trucks | ${location.truck.name} shift on ${format(
          parseISO(stripTimeZone(location.startTime)),
          'M/d/y'
        )}`}
      />

      <div
        className={cn('w-full', {
          'font-albertsons albertsons': albertsons
        })}
      >
        <TruckInformationSection
          content={
            <TruckSectionContent
              location={location}
              preferredVendor={location.truck.preferredVendor}
              preferredVendorBadge={location.truck.preferredVendorBadge}
              ratingInfo={location.truck.ratingInfo}
              scheduleUrl={location.truck.scheduleUrl}
              socialLinks={heroLinks}
              truckName={location.truck.name}
            />
          }
          imageSrc={location.truck.image.original!}
          links={<LinksSection socialLinks={heroLinks} />}
          size="small"
        />
        <div>
          <ShiftMenu location={location} hideLocationInformation />
        </div>
        {!albertsons && (
          <div>
            <AppAds />
          </div>
        )}
      </div>
    </PageLayout>
  )
}

const ShiftPage = (props: Props) => <BaseShiftPage {...props} />

const AlbertsonsShiftPage = (props: Props) => {
  return (
    <ShiftPageContext.Provider value={{ albertsons: true }}>
      <BaseShiftPage {...props} />
    </ShiftPageContext.Provider>
  )
}

export const AlbertsonsShiftPageWithApollo = withApollo({ ssr: false })(
  AlbertsonsShiftPage as NextPage
)

export default withApollo({ ssr: false })(ShiftPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(ShiftPage, {
  revalidate: 60 // 1 minute
})

export const getStaticPropsAlbersons = getStaticApolloProps<Props, Params>(
  AlbertsonsShiftPage,
  {
    revalidate: 60 // 1 minute
  }
)
